import { DataGridWithFilter } from "components/DataGrid";
import { DataGridColumnConfig, DataGridRowConfig } from "components/DataGrid/types";
import { DataGridHeader } from "components/DataGrid/DataGridHeader";
import { DataGridFilter } from "components/DataGrid/DataGridFilter";
import { DataGridBody } from "components/DataGrid/DataGridBody";
import { getColumnSortState, getVisibleColumns } from "components/DataGrid/utils";
import { Link } from "react-router-dom";
import { PageLink } from "components/App/utils";
import { RelatedApplicationListColumnKeys } from "./types";
import { SubmitButton } from "components/Button/SubmitButton";
import { Button } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { isNil } from "lodash";
import { Modal } from "react-bootstrap";
import { relateApplication } from "components/utils";
import { getErrorMessage } from "components/utils/http";
import { useToast } from "components/Toast";
import { DataGridHead } from "components/DataGrid/DataGridHead";

export const RelatedApplicationsGrid: React.FC<{
    gridConfig: any;
    items: any[] | undefined;
    sortBy: string;
    sortAsc: boolean;
    totalRecords?: number;
    onSortChange: (key: string) => void;
    onFilterChange: (filter: string) => void;
    isLoadingGrid: boolean;
    applicationNumber: string;
    onRelationsUpdated: () => void;
    setPageNumber: React.Dispatch<React.SetStateAction<number>>;
    pageNumber: number;
}> = ({
    items,
    gridConfig,
    sortBy,
    sortAsc,
    totalRecords,
    onSortChange,
    onFilterChange,
    isLoadingGrid,
    applicationNumber,
    onRelationsUpdated,
    setPageNumber,
    pageNumber,
}) => {
    const [targetAppId, setTargetAppId] = useState<string | null>();
    const showMessageModal = !isNil(targetAppId);
    const columns = getVisibleColumns(gridConfig);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useToast();

    const removeRelation = async () => {
        let response;
        setIsSubmitting(true);
        try {
            if (targetAppId) {
                response = await relateApplication(applicationNumber, [targetAppId]);
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setTargetAppId(null);
            setIsSubmitting(false);
            toast.success(response?.responseMessage);
            onRelationsUpdated();

            // Navigate to previous page if the last item was deleted
            if (items && items?.length === 1 && pageNumber > 1) {
                setPageNumber(pageNumber - 1);
            }
        }
    };

    const rows: DataGridRowConfig[] = (items || []).map((item, index) => ({
        index,
        data: item,
        actions: [
            {
                content: (row: DataGridRowConfig, actionIndex: number) => (
                    <div key={actionIndex} className="text-end d-flex align-items-center justify-content-start gap-2">
                        <Button
                            variant="link"
                            className="text-danger text-decoration-none"
                            onClick={() => setTargetAppId(row.data[RelatedApplicationListColumnKeys.appId])}
                            title="Remove Relation"
                            aria-haspopup="dialog"
                        >
                            <FontAwesomeIcon icon={["fal", "link-slash"]} size="lg" />
                        </Button>
                        {item.hasAccess && (
                            <>
                                <div className="vr button-divider mx-2 h-50 align-self-center" />
                                <Link
                                    key={actionIndex}
                                    className="text-nowrap d-flex align-items-center p-2 ps-0 gap-2"
                                    to={`${PageLink.ApplicationView}?applicationNumber=${row.data[RelatedApplicationListColumnKeys.appId]}`}
                                    title="View Application"
                                >
                                    <FontAwesomeIcon icon={["fal", "circle-arrow-right"]} size="lg" />
                                </Link>
                            </>
                        )}
                    </div>
                ),
            },
        ],
    }));

    const formattedColumns: DataGridColumnConfig[] = columns.map((col) => ({
        ...col,
        sort: getColumnSortState(col, sortBy, sortAsc),
        name: col.name.split("_").join(" "),
    }));

    return (
        <>
            <DataGridWithFilter
                onFilterChange={onFilterChange}
                onSortChange={onSortChange}
                gridConfig={gridConfig}
                className="align-middle"
                withWrapper={false}
            >
                {({
                    isFilterOpen,
                    isSortOpen,
                    isFilterChanged,
                    isFilterEmpty,
                    toggleFilter,
                    toggleSort,
                    getFilterValue,
                    onFilterValueChange,
                    onFilterValueKeyDown,
                    onFieldClean,
                    onFilterSubmit,
                    onFilterClear,
                    onSortClear,
                    onFilterInputFocus,
                    filterSelected,
                    filter,
                    setFilterInputRef,
                }) => (
                    <>
                        <DataGridHead className="position-sticky sticky-top">
                            <DataGridHeader
                                toggleFilter={toggleFilter}
                                toggleSort={toggleSort}
                                isFilterOpen={isFilterOpen}
                                isSortOpen={isSortOpen}
                                isFilterEmpty={isFilterEmpty}
                                filter={filter}
                                sortBy={sortBy}
                                sortAsc={sortAsc}
                                columns={formattedColumns}
                                onSortChange={onSortChange}
                                totalRecords={totalRecords}
                                onFilterClear={onFilterClear}
                                onSortClear={onSortClear}
                                isFilterChanged={isFilterChanged}
                                getFilterValue={getFilterValue}
                                onFieldClean={onFieldClean}
                                onFilterSubmit={onFilterSubmit}
                                onFilterInputFocus={onFilterInputFocus}
                            />
                            <DataGridFilter
                                setFilterInputRef={setFilterInputRef}
                                toggleFilter={toggleFilter}
                                toggleSort={toggleSort}
                                isFilterOpen={isFilterOpen}
                                isSortOpen={isSortOpen}
                                isFilterEmpty={false}
                                columns={columns}
                                filter={filter}
                                sortBy={sortBy}
                                sortAsc={sortAsc}
                                onFilterChange={onFilterChange}
                                getFilterValue={getFilterValue}
                                onFilterValueChange={onFilterValueChange}
                                onFilterValueKeyDown={onFilterValueKeyDown}
                                onFilterClear={onFilterClear}
                                onSortClear={onSortClear}
                                onFieldClean={onFieldClean}
                                onFilterSubmit={onFilterSubmit}
                                filterSelected={filterSelected}
                                onFilterInputFocus={onFilterInputFocus}
                                onSortChange={onSortChange}
                            />
                        </DataGridHead>
                        <DataGridBody
                            rows={rows}
                            columns={formattedColumns}
                            isLoadingData={isLoadingGrid}
                            isFilterEmpty={isFilterEmpty}
                            onFilterClear={onFilterClear}
                        />
                    </>
                )}
            </DataGridWithFilter>
            <Modal show={showMessageModal} onHide={() => setTargetAppId(undefined)} aria-labelledby="remove-relation-title">
                <Modal.Header closeButton>
                    <Modal.Title id="remove-relation-title">Remove Relation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to remove this application relation?</p>
                </Modal.Body>
                <Modal.Footer>
                    <SubmitButton onClick={removeRelation} isSubmitting={isSubmitting} variant="primary" spinnerText="Is Removing...">
                        Yes
                    </SubmitButton>
                    <Button onClick={() => setTargetAppId(undefined)} variant="secondary">
                        No
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
